/**
 * AdminMessageEditor Component - Create/Edit form for messages
 *
 * Features:
 * - Basic info (type, title)
 * - Content (text/markdown or media)
 * - Display settings (modal size, priority)
 * - Action configuration
 * - Targeting options
 */

import React, { useState } from 'react';
import * as adminMessagesApi from '../../../api/adminMessages';
import { EXPLAINER_MESSAGE_TYPE } from '../../../utils/engagementShow';
import TargetingRuleBuilder from './TargetingRuleBuilder';
import TargetingPreview from './TargetingPreview';
import './AdminMessageEditor.css';

const MESSAGE_TYPES = [
  { value: 'announcement', label: 'Announcement' },
  { value: 'welcome', label: 'Welcome' },
  { value: 'feature', label: 'New Feature' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'beta', label: 'Beta' },
  { value: EXPLAINER_MESSAGE_TYPE, label: 'Explainer' },
  { value: 'hint', label: 'Hint (craft help)' },
];

const ACTION_TYPES = [
  { value: 'dismiss', label: 'Dismiss (close modal)' },
  { value: 'navigate', label: 'Navigate (in-app link)' },
  { value: 'external', label: 'External (new tab)' },
];

function AdminMessageEditor({ message, onSave, onCancel }) {
  const isEditing = !!message?.id;

  const [formData, setFormData] = useState({
    message_type: message?.message_type || 'announcement',
    hint_key: message?.targeting_rules?.hint_key || '',
    title: message?.title || '',
    content: message?.content || '',
    content_type: message?.content_type || 'text',
    media_url: message?.media_url || '',
    media_thumbnail_url: message?.media_thumbnail_url || '',
    modal_size: message?.modal_size || 'standard',
    priority: message?.priority ?? 100,
    primary_action_label: message?.primary_action_label || '',
    primary_action_type: message?.primary_action_type || 'dismiss',
    primary_action_url: message?.primary_action_url || '',
    show_dont_show_again: message?.show_dont_show_again || false,
    target_all_users: message?.target_all_users ?? true,
    target_new_users_only: message?.target_new_users_only || false,
    target_min_sessions: message?.target_min_sessions ?? '',
    target_max_sessions: message?.target_max_sessions ?? '',
    expires_at: message?.expires_at ? message.expires_at.split('T')[0] : '',
    // New fields for dynamic targeting and scheduling
    targeting_rules: message?.targeting_rules || null,
    maintenance_time: message?.maintenance_time ? message.maintenance_time.slice(0, 16) : '',
    publish_at: message?.publish_at ? message.publish_at.slice(0, 16) : '',
    is_urgent: message?.is_urgent || false,
    repeat_daily: message?.repeat_daily || false,
  });

  // Track whether to use dynamic targeting vs legacy
  const [useAdvancedTargeting, setUseAdvancedTargeting] = useState(
    !!message?.targeting_rules?.conditions?.length
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      if (name === 'message_type' && value === EXPLAINER_MESSAGE_TYPE) {
        next.is_urgent = false;
      }
      if (name === 'message_type' && value === 'hint') {
        next.is_urgent = false;
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Prepare data for API
      const data = {
        ...formData,
        priority: parseInt(formData.priority, 10) || 100,
        target_min_sessions: formData.target_min_sessions ? parseInt(formData.target_min_sessions, 10) : null,
        target_max_sessions: formData.target_max_sessions ? parseInt(formData.target_max_sessions, 10) : null,
        expires_at: formData.expires_at || null,
        media_thumbnail_url: formData.media_thumbnail_url || null,
        // New fields
        targeting_rules: useAdvancedTargeting ? formData.targeting_rules : null,
        maintenance_time: formData.maintenance_time || null,
        publish_at: formData.publish_at || null,
        is_urgent: formData.message_type === EXPLAINER_MESSAGE_TYPE || formData.message_type === 'hint'
          ? false
          : formData.is_urgent,
      };

      if (formData.message_type === 'hint') {
        const hintKey = (formData.hint_key || '').trim();
        data.targeting_rules = hintKey ? { hint_key: hintKey } : null;
      }

      delete data.hint_key;

      // Remove empty strings
      if (!data.media_url) delete data.media_url;
      if (!data.primary_action_label) delete data.primary_action_label;
      if (!data.primary_action_url) delete data.primary_action_url;

      let result;
      if (isEditing) {
        result = await adminMessagesApi.updateMessage(message.id, data);
      } else {
        result = await adminMessagesApi.createMessage(data);
      }

      onSave(result.data);
    } catch (err) {
      setError(err.message || 'Failed to save message');
    } finally {
      setSaving(false);
    }
  };

  const handleTargetingRulesChange = (rules) => {
    setFormData(prev => ({ ...prev, targeting_rules: rules }));
  };

  return (
    <div className="admin-message-editor">
      <div className="admin-message-editor__header">
        <h2>{isEditing ? 'Edit Message' : 'Create New Message'}</h2>
        <button className="btn-ghost" onClick={onCancel}>Cancel</button>
      </div>

      {error && (
        <div className="admin-message-editor__error">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="admin-message-editor__form">
        {/* Basic Info */}
        <section className="form-section">
          <h3>Basic Info</h3>
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="message_type">Type</label>
              <select
                id="message_type"
                name="message_type"
                value={formData.message_type}
                onChange={handleChange}
              >
                {MESSAGE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              {formData.message_type === EXPLAINER_MESSAGE_TYPE && (
                <span className="form-hint">
                  After you publish it, Open explainer shows the pop-up. A writer can also open any page with ?explainer= and the message id. It does not pop up on its own, and closing it does not close it for good.
                </span>
              )}
            </div>
            {formData.message_type === 'hint' && (
              <div className="form-field">
                <label htmlFor="hint_key">Hint key</label>
                <input
                  type="text"
                  id="hint_key"
                  name="hint_key"
                  value={formData.hint_key}
                  onChange={handleChange}
                  required
                  maxLength={64}
                  placeholder="B1"
                />
                <span className="form-hint">Matches the front-end spot key (e.g. B1, S5, callout-1).</span>
              </div>
            )}
            <div className="form-field form-field--wide">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                maxLength={255}
                placeholder="Message title"
              />
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="form-section">
          <h3>Content</h3>
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="content_type">Content Type</label>
              <select
                id="content_type"
                name="content_type"
                value={formData.content_type}
                onChange={handleChange}
              >
                <option value="text">Text/Markdown</option>
                <option value="media">Video + Text</option>
              </select>
            </div>
          </div>

          {formData.content_type === 'media' && (
            <>
              <div className="form-field">
                <label htmlFor="media_url">Video URL (YouTube or Vimeo)</label>
                <input
                  type="url"
                  id="media_url"
                  name="media_url"
                  value={formData.media_url}
                  onChange={handleChange}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>
              <div className="form-field">
                <label htmlFor="media_thumbnail_url">Thumbnail URL (optional)</label>
                <input
                  type="url"
                  id="media_thumbnail_url"
                  name="media_thumbnail_url"
                  value={formData.media_thumbnail_url}
                  onChange={handleChange}
                  placeholder="https://..."
                />
                <span className="form-hint">A YouTube link already has a thumbnail. Paste one for Vimeo.</span>
              </div>
            </>
          )}

          <div className="form-field">
            <label htmlFor="content">Content (Markdown supported)</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              required
              rows={6}
              placeholder="Message content..."
            />
          </div>
        </section>

        {/* Display */}
        <section className="form-section">
          <h3>Display</h3>
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="modal_size">Modal Size</label>
              <select
                id="modal_size"
                name="modal_size"
                value={formData.modal_size}
                onChange={handleChange}
              >
                <option value="compact">Compact</option>
                <option value="standard">Standard</option>
              </select>
              <span className="form-hint">Centered, and it fills most of the window.</span>
            </div>
            <div className="form-field">
              <label htmlFor="priority">Priority</label>
              <input
                type="number"
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                min={0}
                max={999}
              />
              <span className="form-hint">Higher = shown first</span>
            </div>
          </div>
        </section>

        {/* Action */}
        <section className="form-section">
          <h3>Action Button</h3>
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="primary_action_label">Button Label</label>
              <input
                type="text"
                id="primary_action_label"
                name="primary_action_label"
                value={formData.primary_action_label}
                onChange={handleChange}
                placeholder="Got it"
              />
            </div>
            <div className="form-field">
              <label htmlFor="primary_action_type">Action Type</label>
              <select
                id="primary_action_type"
                name="primary_action_type"
                value={formData.primary_action_type}
                onChange={handleChange}
              >
                {ACTION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {formData.primary_action_type !== 'dismiss' && (
            <div className="form-field">
              <label htmlFor="primary_action_url">URL</label>
              <input
                type="url"
                id="primary_action_url"
                name="primary_action_url"
                value={formData.primary_action_url}
                onChange={handleChange}
                placeholder={formData.primary_action_type === 'navigate' ? '/settings' : 'https://...'}
              />
            </div>
          )}

          <div className="form-field form-field--checkbox">
            <label>
              <input
                type="checkbox"
                name="show_dont_show_again"
                checked={formData.show_dont_show_again}
                onChange={handleChange}
              />
              Show "Don't show this again" checkbox
            </label>
          </div>
        </section>

        {/* Targeting */}
        <section className="form-section">
          <h3>Targeting</h3>

          <div className="form-field form-field--checkbox">
            <label>
              <input
                type="checkbox"
                checked={useAdvancedTargeting}
                onChange={(e) => setUseAdvancedTargeting(e.target.checked)}
              />
              Use advanced targeting rules
            </label>
          </div>

          {useAdvancedTargeting ? (
            <>
              <TargetingRuleBuilder
                value={formData.targeting_rules}
                onChange={handleTargetingRulesChange}
              />
              <TargetingPreview rules={formData.targeting_rules} />
            </>
          ) : (
            <>
              <div className="form-field form-field--checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="target_all_users"
                    checked={formData.target_all_users}
                    onChange={handleChange}
                  />
                  Target all users
                </label>
              </div>

              {formData.target_all_users && (
                <div className="form-field form-field--checkbox">
                  <label>
                    <input
                      type="checkbox"
                      name="target_new_users_only"
                      checked={formData.target_new_users_only}
                      onChange={handleChange}
                    />
                    New users only (signed up after publish)
                  </label>
                </div>
              )}

              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="target_min_sessions">Min Sessions</label>
                  <input
                    type="number"
                    id="target_min_sessions"
                    name="target_min_sessions"
                    value={formData.target_min_sessions}
                    onChange={handleChange}
                    min={0}
                    placeholder="Any"
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="target_max_sessions">Max Sessions</label>
                  <input
                    type="number"
                    id="target_max_sessions"
                    name="target_max_sessions"
                    value={formData.target_max_sessions}
                    onChange={handleChange}
                    min={0}
                    placeholder="Any"
                  />
                </div>
              </div>
            </>
          )}
        </section>

        {/* Scheduling */}
        <section className="form-section">
          <h3>Scheduling</h3>

          <div className="form-field form-field--checkbox">
            <label>
              <input
                type="checkbox"
                name="is_urgent"
              checked={formData.message_type === EXPLAINER_MESSAGE_TYPE || formData.message_type === 'hint'
                ? false
                : formData.is_urgent}
              onChange={handleChange}
              disabled={formData.message_type === EXPLAINER_MESSAGE_TYPE || formData.message_type === 'hint'}
            />
              Urgent message (enables real-time polling)
            </label>
              <span className="form-hint">
              {formData.message_type === EXPLAINER_MESSAGE_TYPE
                ? 'An explainer is not an urgent message.'
                : formData.message_type === 'hint'
                  ? 'A hint row is not an urgent message.'
                  : 'Urgent messages are polled every 30 seconds and shown immediately'}
            </span>
          </div>

          <div className="form-field form-field--checkbox">
            <label>
              <input
                type="checkbox"
                name="repeat_daily"
                checked={formData.repeat_daily}
                onChange={handleChange}
              />
              If closed, show again the next day
            </label>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label htmlFor="publish_at">Publish At (optional)</label>
              <input
                type="datetime-local"
                id="publish_at"
                name="publish_at"
                value={formData.publish_at}
                onChange={handleChange}
              />
              <span className="form-hint">Schedule message to publish later</span>
            </div>
            <div className="form-field">
              <label htmlFor="expires_at">Expires At (optional)</label>
              <input
                type="date"
                id="expires_at"
                name="expires_at"
                value={formData.expires_at}
                onChange={handleChange}
              />
            </div>
          </div>

          {formData.message_type === 'maintenance' && (
            <div className="form-field">
              <label htmlFor="maintenance_time">Maintenance Start Time</label>
              <input
                type="datetime-local"
                id="maintenance_time"
                name="maintenance_time"
                value={formData.maintenance_time}
                onChange={handleChange}
              />
              <span className="form-hint">Countdown will be shown to users</span>
            </div>
          )}
        </section>

        {/* Actions */}
        <div className="admin-message-editor__actions">
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Draft')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminMessageEditor;
